"use client";

import { useState, useEffect } from 'react';
import { Card, Tag } from '@/components/ds';
import UserAvatar from '@/components/UserAvatar';
import { useAuthContext } from '@/components/providers/AuthProvider';
import {
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  MoreHorizontal,
  Trash2,
  Edit,
  Flag,
  Copy,
  Tag as TagIcon,
} from 'lucide-react';
import Link from 'next/link';
import { useLiveTimestamp } from '@/hooks/useLiveTimestamp';
import { CommunityPost } from '@/types';
import ShareModal from '@/components/ShareModal';
import EditPostModal from '@/components/EditPostModal';
import ReportContentModal from '@/components/ReportContentModal';
import PostComments from '@/components/PostComments';
import SeamlessMediaDisplay from '@/components/SeamlessMediaDisplay';
import PostImageGrid from '@/components/PostImageGrid';
import YouTubePreview from '@/components/posts/YouTubePreview';
import { stripYouTubeUrls } from '@/lib/youtube';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface PostItemProps {
  post: CommunityPost;
  currentUserId?: string;
  onLike: (postId: string) => void;
  onSave: (postId: string) => void;
  onDelete: (postId: string) => void;
  onCopyLink: (postId: string) => void;
  getCategoryIcon: (category: string) => any;
  getCategoryColor: (category: string) => string;
  onPostUpdated?: () => void;
  /** When true, the inline comments section is open from first paint.
   *  Used by the post-detail page (/community/posts/[postId]) so the
   *  thread is visible without an extra click. Defaults to false in
   *  every feed context. */
  defaultCommentsOpen?: boolean;
}

export default function PostItem({
  post,
  currentUserId,
  onLike,
  onSave,
  onDelete,
  onCopyLink,
  getCategoryIcon,
  onPostUpdated,
  defaultCommentsOpen = false,
}: PostItemProps) {
  const { user } = useAuthContext();
  const liveTimestamp = useLiveTimestamp(post.createdAt);
  const isOwnPost = currentUserId === post.userId;
  const category = post.category || 'general';
  const CategoryIcon = getCategoryIcon(category);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [hiddenAfterReport, setHiddenAfterReport] = useState(false);
  const [commentsRefreshTrigger] = useState(0);
  const [commentsOpen, setCommentsOpen] = useState(defaultCommentsOpen);
  const [isLiking, setIsLiking] = useState(false);
  const [hasLiked, setHasLiked] = useState(!!post.isLiked);
  const [likeCount, setLikeCount] = useState(post.likes ?? 0);

  useEffect(() => {
    setHasLiked(!!post.isLiked);
    setLikeCount(post.likes ?? 0);
  }, [post.isLiked, post.likes]);

  useEffect(() => {
    if (!user?.id) return;
    const check = async () => {
      try {
        const res = await fetch(`/api/community/posts/${post.id}/like-status`);
        if (res.ok) {
          const data = await res.json();
          setHasLiked(!!data.hasLiked);
        }
      } catch {
        // keep current state
      }
    };
    check();
  }, [post.id, user?.id]);

  const handleLike = async () => {
    if (!user || isLiking) return;
    setIsLiking(true);
    const nextLiked = !hasLiked;
    setHasLiked(nextLiked);
    setLikeCount((c) => (nextLiked ? c + 1 : Math.max(0, c - 1)));
    try {
      await onLike(post.id);
    } catch {
      setHasLiked(!nextLiked);
      setLikeCount((c) => (nextLiked ? c - 1 : c + 1));
    } finally {
      setIsLiking(false);
    }
  };

  const openComments = () => {
    setCommentsOpen(true);
    // Focus the textarea after the comments DOM mounts.
    setTimeout(() => {
      const section = document.getElementById(`comments-${post.id}`);
      const textarea = section?.querySelector('textarea') as HTMLTextAreaElement | null;
      textarea?.focus();
      section?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  if (hiddenAfterReport) {
    return null;
  }

  return (
    <Card padded={false} className="p-3 sm:p-4">
      <div className="flex items-start gap-2.5 sm:gap-3">
        <UserAvatar
          user={{
            avatar: post.user.avatar,
            name: post.user.name,
            username: post.user.username,
          }}
          size="sm"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
            {(() => {
              // Author handle in the post payload may include a leading '@'
              // ('@karl') OR be bare ('karl'). The /u/[username] route wants
              // the bare form. Strip any leading '@' before building the URL.
              const handle = (post.user.username ?? '').replace(/^@/, '');
              const profileHref = handle ? `/u/${handle}` : null;
              const NameAndHandle = (
                <>
                  <span className="font-semibold text-text-primary text-sm truncate max-w-[140px] sm:max-w-none">
                    {post.user.name}
                  </span>
                  <span className="text-text-secondary text-xs truncate max-w-[100px] sm:max-w-none">
                    {post.user.username}
                  </span>
                </>
              );
              return profileHref ? (
                <Link
                  href={profileHref}
                  className="inline-flex items-center gap-1.5 min-w-0 hover:[&>span:first-child]:underline"
                >
                  {NameAndHandle}
                </Link>
              ) : (
                NameAndHandle
              );
            })()}
            <span className="text-text-tertiary text-xs">·</span>
            <span className="text-text-tertiary text-xs">{liveTimestamp}</span>
            <Tag variant="accent">
              <CategoryIcon className="h-3 w-3" strokeWidth={1.75} />
              {category}
            </Tag>
          </div>

          {(() => {
            const previews = post.previews ?? [];
            const stripIds = new Set(previews.map((p) => p.video_id));
            const displayContent =
              stripIds.size > 0 ? stripYouTubeUrls(post.content, stripIds) : post.content;
            return (
              <>
                {displayContent && (
                  <p
                    className="text-text-primary text-sm leading-relaxed mb-2.5 break-words"
                    style={{ overflowWrap: 'anywhere' }}
                  >
                    {displayContent}
                  </p>
                )}
                {previews.length > 0 && (
                  <div className="mb-2.5 space-y-2">
                    {previews.map((p) => (
                      <YouTubePreview
                        key={p.video_id}
                        videoId={p.video_id}
                        title={p.title}
                        authorName={p.author_name}
                        thumbnailUrl={p.thumbnail_url}
                      />
                    ))}
                  </div>
                )}
              </>
            );
          })()}

          {post.media && post.media.urls && post.media.urls.length > 0 ? (
            <div className="mb-2.5">
              <PostImageGrid urls={post.media.urls} />
            </div>
          ) : post.media && post.media.url ? (
            <div className="mb-2.5">
              <SeamlessMediaDisplay
                file_url={post.media.url}
                file_type={post.media.type}
                thumbnail_url={post.media.thumbnail}
                title={
                  post.content.substring(0, 50) +
                  (post.content.length > 50 ? '...' : '')
                }
                className="w-full"
              />
            </div>
          ) : null}

          <div className="flex items-center justify-between pt-1.5 border-t border-border-subtle">
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                type="button"
                disabled={isLiking || !user}
                onClick={handleLike}
                className={cn(
                  'flex items-center gap-1 transition-colors disabled:opacity-50',
                  hasLiked
                    ? 'text-status-busy'
                    : 'text-text-secondary hover:text-status-busy'
                )}
                aria-label="Like"
              >
                <Heart
                  className="h-4 w-4"
                  strokeWidth={1.75}
                  fill={hasLiked ? 'currentColor' : 'none'}
                />
                <span className="text-xs font-medium tabular-nums">{likeCount}</span>
              </button>
              <button
                type="button"
                onClick={openComments}
                className="flex items-center gap-1 text-text-secondary hover:text-text-primary transition-colors"
                aria-label="Comments"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
                <span className="text-xs font-medium tabular-nums">{post.comments ?? 0}</span>
              </button>
              <button
                type="button"
                onClick={() => setShareModalOpen(true)}
                className="flex items-center gap-1 text-text-secondary hover:text-text-primary transition-colors"
                aria-label="Share"
              >
                <Share2 className="h-4 w-4" strokeWidth={1.75} />
                <span className="text-xs font-medium tabular-nums">{post.shares ?? 0}</span>
              </button>
            </div>

            <button
              type="button"
              onClick={() => onSave(post.id)}
              className={cn(
                'transition-colors',
                post.isSaved
                  ? 'text-accent'
                  : 'text-text-secondary hover:text-text-primary'
              )}
              aria-label="Save"
            >
              <Bookmark
                className="h-4 w-4"
                strokeWidth={1.75}
                fill={post.isSaved ? 'currentColor' : 'none'}
              />
            </button>
          </div>

          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {post.tags.map((t) => (
                <Link key={t} href={`/tags?tag=${encodeURIComponent(t)}`}>
                  <Tag>
                    <TagIcon className="h-3 w-3" strokeWidth={1.75} />
                    {t}
                  </Tag>
                </Link>
              ))}
            </div>
          )}

          {commentsOpen && (
            <div id={`comments-${post.id}`} data-post-id={post.id}>
              <PostComments
                postId={post.id}
                postAuthorName={post.user.name}
                maxComments={999}
                refreshTrigger={commentsRefreshTrigger}
              />
            </div>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="text-text-secondary hover:text-text-primary p-1 transition-colors"
              aria-label="More"
            >
              <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-surface-1 border-border-subtle text-text-primary"
          >
            {isOwnPost && (
              <>
                <DropdownMenuItem
                  className="text-text-primary hover:bg-surface-2 cursor-pointer"
                  onClick={() => setEditModalOpen(true)}
                >
                  <Edit className="h-4 w-4 mr-2" strokeWidth={1.75} />
                  Edit Post
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-status-busy hover:bg-surface-2 cursor-pointer"
                  onClick={() => onDelete(post.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.75} />
                  Delete Post
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border-subtle" />
              </>
            )}
            <DropdownMenuItem
              className="text-text-primary hover:bg-surface-2 cursor-pointer"
              onClick={() => onCopyLink(post.id)}
            >
              <Copy className="h-4 w-4 mr-2" strokeWidth={1.75} />
              Copy Link
            </DropdownMenuItem>
            {!isOwnPost && (
              <DropdownMenuItem
                className="text-text-primary hover:bg-surface-2 cursor-pointer"
                onClick={() => setReportModalOpen(true)}
              >
                <Flag className="h-4 w-4 mr-2" strokeWidth={1.75} />
                Report Post
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ShareModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        postId={post.id}
        isProfilePost={false}
        onShareComplete={() => {}}
      />

      {!isOwnPost && (
        <ReportContentModal
          open={reportModalOpen}
          onOpenChange={setReportModalOpen}
          targetType="post"
          targetId={post.id}
          targetUserId={post.userId}
          targetLabel={post.content.slice(0, 60)}
          onSubmitted={() => setHiddenAfterReport(true)}
        />
      )}

      {isOwnPost && (
        <EditPostModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          post={post}
          onPostUpdated={() => {
            onPostUpdated?.();
          }}
        />
      )}
    </Card>
  );
}
